import { p as proxyCustomElement, H, c as createEvent, h, a as Host } from './index.js';
import { d as dateFnsExports } from './p-iWCgRMLC.js';
import { i as isDefined } from './p-D_OOeJgV.js';
import { d as defineCustomElement$5 } from './p-DhNzpZvu.js';
import { d as defineCustomElement$4 } from './p-Dr61d_nc.js';
import { d as defineCustomElement$3 } from './p-D-dZjG8L.js';
import { d as defineCustomElement$2 } from './p-_P4kPR3l.js';
import { d as defineCustomElement$1 } from './p-CdhcNtiX.js';

function memoize(fn, options = {}) {
    const { cache = new Map(), getCacheKey } = options;
    const memoizedFn = function (arg) {
        const key = getCacheKey ? getCacheKey(arg) : arg;
        if (cache.has(key)) {
            return cache.get(key);
        }
        const result = fn.call(this, arg);
        cache.set(key, result);
        return result;
    };
    memoizedFn.cache = cache;
    return memoizedFn;
}

//#region src/module/format/date/index.ts
const toDate = (input) => {
	if (typeof input === "undefined") return;
	if (input === null) return null;
	if (input instanceof Date) return input;
	if (!input) return;
	if (isValidDateString(input)) {
		const [year, month, day] = input.split("-").map((i) => Number.parseInt(i, 10));
		return new Date(year, month - 1, day);
	}
	throw new Error("Invalid date string");
};
/**
* Convert Date or ISOString to date string: yyyy-mm-dd
*
* When passing `Date`: always uses local TZ, not UTC!
*/
function toDateString(input) {
	if (typeof input === "undefined") return;
	if (input === null) return null;
	if (typeof input === "string") {
		if (!input) return;
		if (!(isValidDateString(input) || isValidDateTimeString(input))) throw new Error("Invalid date(time) string");
		return input.substring(0, 10);
	}
	return [
		input.getFullYear(),
		input.getMonth() + 1,
		input.getDate()
	].map((v) => `${v}`.padStart(2, "0")).join("-");
}
function isValidDateTimeString(input) {
	if (typeof input === "undefined") return false;
	let [date, time] = input.split("T");
	time = time?.replace(/(Z)|((\+|-)\d{2}:\d{2})$/gm, "");
	return isValidDateString(date) && isValidTimeString(time);
}
function isValidDateString(input) {
	if (typeof input !== "string") return false;
	const parts = input.split("-");
	if (parts.length !== 3) return false;
	const [y, m, d] = parts;
	if (!(typeof y === "string" && typeof m === "string" && typeof d === "string")) return false;
	if (!(y.length === 4 && +y > 0)) return false;
	if (!(m.length === 2 && +m > 0 && +m <= 12)) return false;
	if (!(d.length === 2 && +d > 0 && +d <= 31)) return false;
	return true;
}
function isValidTimeString(input) {
	if (typeof input !== "string") return false;
	if (input.match(/(Z)|((\+|-)\d{2}:\d{2})$/gm) !== null) return false;
	const parts = input.split(":");
	if (!((p) => {
		return p.length === 2 || p.length === 3;
	})(parts)) return false;
	const [h, m, _s] = parts;
	if (!(h.length === 2 && +h < 24)) return false;
	if (!(m.length === 2 && +m < 60)) return false;
	if (_s) {
		const [s, ms] = _s.split(".");
		if (!(s.length === 2 && +s < 60)) return false;
		if (ms) {
			if (!(ms.length === 3 && +ms < 1e3)) return false;
		}
	}
	return true;
}

const FORMATS = {
    // Friendly: Short (June 18, 2019)
    FRIENDLY_SHORT: 'MMM dd, yyyy',
    // Friendly: Medium (Tuesday, June 18, 2019)
    FRIENDLY_MEDIUM: 'iiii, MMMM d, yyyy',
    // ISO 8601: DateTime (2019-06-18T00:00:00+00:00)
    ISO_FULL: 'yyyy-MM-dd[T]HH:mm:ssZ',
    // ISO 8601: Date (2019-06-18)
    ISO_DAY: 'yyyy-MM-dd',
    // Calendar: Day (18)
    CAL_DAY: 'dd',
    // Calendar: Header (month + year) (June 2019)
    CAL_MONTH_YEAR: 'MMMM yyyy',
    DEBUG: 'ddd, dd MMM yyyy HH:mm:ss z',
};
const format = (type, date) => dateFnsExports.format(date, FORMATS[type]);

//#region src/module/format/date-range/index.ts
function isValidValueInDateRange(v) {
	return v === null || typeof v === "string" && isValidDateString(v);
}
function toDateRange(arg) {
	if (typeof arg === "undefined") return;
	if (arg === null) return null;
	if (typeof arg === "string") {
		const [start$1, end$1] = JSON.parse(arg);
		if (!isValidValueInDateRange(start$1)) throw new Error("Invalid start date");
		if (!isValidValueInDateRange(end$1)) throw new Error("Invalid end date");
		return [start$1, end$1];
	}
	const [start, end] = arg;
	return [start instanceof Date ? toDateString(start) : start, end instanceof Date ? toDateString(end) : end];
}
function toDateRangeString(dateRange) {
	if (typeof dateRange === "undefined") return;
	if (dateRange === null) return null;
	const [start, end] = dateRange;
	return JSON.stringify([start ? toDateString(start) : start, end ? toDateString(end) : end]);
}
function getDaysInRange(range) {
	const [start, end] = toDateRange(range) ?? [null, null];
	if (start === null || end === null) return 0;
	if (isValidValueInDateRange(start) && isValidValueInDateRange(end)) return dateFnsExports.eachDayOfInterval({
		start,
		end
	}).length;
	return 0;
}
function exceedsMaxDays(range, maxDays) {
	if (typeof maxDays !== "number" || !range) return false;
	if (getDaysInRange(range) > maxDays) return true;
	return false;
}
function isRangeBeforeDate(arg, date) {
	if (!date) return false;
	const [start, end] = toDateRange(arg);
	if (start && dateFnsExports.isBefore(start, date)) return true;
	if (end && dateFnsExports.isBefore(end, date)) return true;
	return false;
}
function isRangeAfterDate(arg, date) {
	if (!date) return false;
	const [start, end] = toDateRange(arg);
	if (start && dateFnsExports.isAfter(start, date)) return true;
	if (end && dateFnsExports.isAfter(end, date)) return true;
	return false;
}
function isRangeInOrder(range) {
	const [start, end] = toDateRange(range) ?? [null, null];
	if (!(start && end)) return true;
	if (start === end) return true;
	if (dateFnsExports.isBefore(end, start)) return false;
	return true;
}

const DAYS_IN_WEEK = 7;
//
// Since we're dealing with dates, time of day is irrelevant.
// That's why we're wrapping the following date-fns methods
// to include `isSameDay`.
//
const isBefore = (date, compareDate) => {
    if (dateFnsExports.isSameDay(date, compareDate)) {
        return false;
    }
    return dateFnsExports.isBefore(date, compareDate);
};
const isAfter = (date, compareDate) => {
    if (dateFnsExports.isSameDay(date, compareDate)) {
        return false;
    }
    return dateFnsExports.isAfter(date, compareDate);
};
const isWithinInterval = (date, { start, end }) => {
    if (dateFnsExports.isSameDay(date, start) || dateFnsExports.isSameDay(date, end)) {
        return true;
    }
    try {
        // Return within range including same days
        return dateFnsExports.isWithinInterval(date, { start, end });
    }
    catch {
        return false;
    }
};
/**
 * Get array of day objects for month with every week filled to 7 days, even when days are not in the same month.
 *
 * Also accepts passing a string for easy function memoization. String will be parsed by date-fns.
 *
 * @example
 * // May 2018
 * [
 *  { inMonth: false, date: Date('Sunday April 29') },
 *  { inMonth: false, date: Date('Monday April 30') },
 *  { inMonth: true, date: Date('Tuesday May 1') },
 *  ...
 *  { inMonth: true, date: Date('Thurday May 31') },
 *  { inMonth: false, date: Date('Friday June 1') },
 *  { inMonth: false, date: Date('Saturday June 2') }
 * ]
 */
function getCalendarDays({ year, month, weekStartsOn, }) {
    const date = new Date(year, month);
    if (!dateFnsExports.isValid(date)) {
        throw new Error(`Invalid date \`${date}\`!`);
    }
    // Get the first and last days of the month
    const monthStartDate = dateFnsExports.startOfMonth(date);
    const monthEndDate = dateFnsExports.endOfMonth(date);
    // If the first of the month is Tuesday May 1 then the first day of that week is Sunday April 29
    const startDate = dateFnsExports.startOfWeek(monthStartDate, { weekStartsOn });
    // If the last day of the month is Thursday May 31 then the last day of that week is Saturday June 2
    const endDate = dateFnsExports.endOfWeek(monthEndDate, { weekStartsOn });
    const ret = [];
    let d = startDate;
    while (
    // Use a fixed number instead of while true
    ret.length < 100) {
        // Stop adding to ret when we've surpassed last weekday in month
        if (d > endDate) {
            break;
        }
        ret.push({
            inMonth: dateFnsExports.isSameMonth(d, monthStartDate),
            date: d,
            key: +`${d.getFullYear()}${d.getMonth()}${d.getDate()}`,
            formatted: {
                full: format('FRIENDLY_MEDIUM', d),
                day: format('CAL_DAY', d),
            },
        });
        d = dateFnsExports.addDays(d, 1);
    }
    return ret;
}
// Memoize function to return the same array references for each month.
// This will improve change detection and avoid unneeded renders when arrays are identical.
const memoizedGetCalendarDays = memoize(getCalendarDays, {
    getCacheKey(args) {
        return [args.year, args.month, args.weekStartsOn].join(';');
    },
});
const getWeekDayHeaders = (weekStartsOn) => {
    switch (weekStartsOn) {
        case 0:
            // Week starts on Sunday
            return ['S', 'M', 'Tu', 'W', 'Th', 'F', 'S'];
        default:
            // Week starts on Monday (ISO 8601)
            return ['M', 'Tu', 'W', 'Th', 'F', 'S', 'S'];
    }
};
/**
 * Determine whether day is within a range.
 *
 * Note: same day allowed!
 */
function isDateInRange(date, props) {
    // We need one of these to be able to compare a range
    if (!(props.rangeStartDate || props.rangeEndDate)) {
        return false;
    }
    // We disable ranges when there is an error and we're not hovering
    if (props.dateRangeError && !props.hoverDate) {
        return false;
    }
    const rangeStartDate = toDate(props.rangeStartDate);
    const rangeEndDate = toDate(props.rangeEndDate);
    const valueDate = toDate(props.value);
    // The compare date is either what we're hovering on, or the current value
    let compareDate = props.hoverDate || valueDate;
    // If we have both dates
    if (props.hoverDate && valueDate) {
        if (rangeStartDate && isBefore(props.hoverDate, rangeStartDate)) {
            compareDate = valueDate;
        }
        if (rangeEndDate && isAfter(props.hoverDate, rangeEndDate)) {
            compareDate = valueDate;
        }
    }
    if (!compareDate) {
        return false;
    }
    // When selecting end, any date before the start can not be in range
    if (rangeStartDate && isBefore(date, rangeStartDate)) {
        return false;
    }
    // When selecting start, any date after the end can not be in range
    if (rangeEndDate && isAfter(date, rangeEndDate)) {
        return false;
    }
    // Date must be between start and compareDate (end)
    if (rangeStartDate && isWithinInterval(date, { start: rangeStartDate, end: compareDate })) {
        return true;
    }
    if (rangeEndDate && isWithinInterval(date, { start: compareDate, end: rangeEndDate })) {
        return true;
    }
    // Same day also falls in range
    if (dateFnsExports.isSameDay(date, compareDate)) {
        return true;
    }
    return false;
}
/**
 * Determine whether a given date is disabled based on min and/or max date combined with range start and/or end date.
 *
 * Note: same day is allowed!
 */
function isDateDisabled(date, props) {
    // If date is before minDate
    const min = toDate(props.min);
    if (min && isBefore(date, min)) {
        return true;
    }
    // If date is after maxDate
    const max = toDate(props.max);
    if (max && isAfter(date, max)) {
        return true;
    }
    return false;
}
function getMaxDaysDate(props) {
    if (!props.maxDays) {
        return {};
    }
    const maxDays = props.maxDays - 1; // offset by one
    let maxDaysFromRangeStart = props.rangeStartDate ? dateFnsExports.addDays(props.rangeStartDate, maxDays) : undefined;
    // Ensure that a max date always takes precedence
    if (maxDaysFromRangeStart && props.max) {
        maxDaysFromRangeStart = dateFnsExports.min([maxDaysFromRangeStart, props.max]);
    }
    let maxDaysFromRangeEnd = props.rangeEndDate ? dateFnsExports.subDays(props.rangeEndDate, maxDays) : undefined;
    // Ensure that a min date always takes precedence
    if (maxDaysFromRangeEnd && props.min) {
        maxDaysFromRangeEnd = dateFnsExports.max([maxDaysFromRangeEnd, props.min]);
    }
    return {
        maxDaysFromRangeStart,
        maxDaysFromRangeEnd,
    };
}
function isDateOnMaxDays(date, props) {
    if (typeof props.maxDays !== 'number') {
        return;
    }
    const { maxDaysFromRangeStart, maxDaysFromRangeEnd } = getMaxDaysDate(props);
    // If rangeStartDate is valid and date is after rangeStartDate + maxDays
    if (maxDaysFromRangeStart && dateFnsExports.isSameDay(date, maxDaysFromRangeStart)) {
        return 'end';
    }
    // If rangeEndDate is valid and date is before rangeEndDate - maxDays
    if (maxDaysFromRangeEnd && dateFnsExports.isSameDay(date, maxDaysFromRangeEnd)) {
        return 'start';
    }
}
/**
 * Determine whether navigation to previous month is disabled.
 *
 * It will only be disabled if there is NO more available date to select before
 * the currently displayed month.
 */
function isPrevMonthDisabled(displayDate, props) {
    if (!props.min) {
        return false;
    }
    const minDates = [props.min, props.value || undefined].filter(isDefined);
    if (minDates.length) {
        return isAfter(dateFnsExports.min(minDates), dateFnsExports.startOfMonth(displayDate));
    }
    return false;
}
/**
 * Determine whether navigation to next month is disabled.
 *
 * It will only be disabled if there is NO more available date to select after
 * the currently displayed month.
 */
function isNextMonthDisabled(displayDate, props) {
    if (!props.max) {
        return false;
    }
    const maxDates = [props.max, props.value || undefined].filter(isDefined);
    if (maxDates.length) {
        return isBefore(dateFnsExports.max(maxDates), dateFnsExports.endOfMonth(displayDate));
    }
    return false;
}
/**
 * Determine whether a date is the start of a date range.
 */
function isDateRangeStart(date, props) {
    // Prevent start date from being later than the end date
    const rangeEndDate = toDate(props.rangeEndDate);
    if (rangeEndDate && isAfter(date, rangeEndDate)) {
        return false;
    }
    const compareDate = 
    // Fixed range start takes precedence
    props.rangeStartDate ||
        // If there's no fixed range start, there may be a fixed end so try hover date
        (props.hoverDate ? toDateString(props.hoverDate) : undefined) ||
        // If there's no hover date, try current value
        props.value;
    return compareDate ? toDateString(date) === compareDate : false;
}
/**
 * Determine whether a date is the end of a date range.
 */
function isDateRangeEnd(date, props) {
    // Prevent end date from being earlier than the start date
    const rangeStartDate = toDate(props.rangeStartDate);
    if (rangeStartDate && isBefore(date, rangeStartDate)) {
        return false;
    }
    const compareDate = 
    // Fixed range end takes precedence
    props.rangeEndDate ||
        // If there's no fixed range end, there may be a fixed start so try hover date
        (props.hoverDate ? toDateString(props.hoverDate) : undefined) ||
        // If there's no hover date, try current value
        props.value;
    return compareDate ? toDateString(date) === compareDate : false;
}
function getInitialDisplayDate(props) {
    if (props.initialCalendarDisplayDate === 'selected') {
        const valueDate = toDate(props.value);
        if (valueDate) {
            // Return existing value
            return valueDate;
        }
    }
    // Return local start of day
    return dateFnsExports.startOfDay(new Date());
}
function checkDateOverlaps(props) {
    if (props.rangeStartDate && props.rangeEndDate) {
        throw new Error('Can not set both `rangeStartDate` and `rangeEndDate`!');
    }
    if (!props.value) {
        // No errors if there is no value 🎉
        return;
    }
    const valueDate = toDate(props.value);
    // Treat value as range end
    if (props.rangeStartDate) {
        if (isBefore(valueDate, toDate(props.rangeStartDate))) {
            throw new Error(`Range end may not be before range start (${props.rangeStartDate}).`);
        }
        if (exceedsMaxDays([props.value, props.rangeStartDate], props.maxDays)) {
            throw new Error(`Range may not exceed ${props.maxDays} days`);
        }
    }
    if (props.rangeEndDate) {
        // Treat value as range start
        if (isAfter(valueDate, toDate(props.rangeEndDate))) {
            throw new Error(`Range start may not be after range end ${props.rangeEndDate}.`);
        }
        if (exceedsMaxDays([props.value, props.rangeEndDate], props.maxDays)) {
            throw new Error(`Range may not exceed ${props.maxDays} days`);
        }
    }
    if (props.max && isAfter(valueDate, toDate(props.max))) {
        throw new Error(`Value may not be after max date ${props.max}.`);
    }
    if (props.min && isBefore(valueDate, toDate(props.min))) {
        throw new Error(`Value may not be before min date ${props.min}.`);
    }
}
function segmentMonthInWeeks(month) {
    return Array.from({ length: Math.ceil(month.length / DAYS_IN_WEEK) }, (_, index) => month.slice(index * DAYS_IN_WEEK, (index + 1) * DAYS_IN_WEEK));
}

const dateInputCalendarCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;align-items:flex-start;background-color:var(--jw-theme-surface-elevated-default-bg-color);box-sizing:border-box;display:inline-flex;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-weight:400;gap:.25rem;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;padding:1rem}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.month-header{align-items:center;color:var(--jw-theme-surface-text-default-color);font-size:.875rem;justify-content:space-between;letter-spacing:-.0004em;line-height:1.25rem;width:14rem}.month-header,.weekdays-header{display:flex;font-family:Open Sans;font-weight:600}.weekdays-header{font-size:.75rem;letter-spacing:0;line-height:1rem}.weekday-header{align-items:center;color:var(--jw-theme-surface-text-helper-color);display:inline-flex;height:2rem;justify-content:center;width:2rem}.days-container{align-items:flex-start;border-radius:.25rem;display:flex}.day-btn{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;align-items:center;background:none;border:none;box-sizing:border-box;color:var(--jw-theme-surface-text-default-color);cursor:pointer;display:inline-flex;font-family:Open Sans;font-size:.875rem;font-weight:inherit;font-weight:400;height:2rem;justify-content:center;letter-spacing:-.0004em;line-height:normal;line-height:1.25rem;margin:0;padding:0;position:relative;-webkit-user-select:none;user-select:none;width:2rem;z-index:2}.day-btn *,.day-btn :after,.day-btn :before{box-sizing:inherit}.day-btn img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.day-btn:before{background-color:var(--jw-theme-surface-item-active-bg-color);transition:opacity .15s ease-in;z-index:-2}.day-btn:after,.day-btn:before{content:\"\";height:100%;opacity:0;position:absolute;right:0;top:0;width:100%}.day-btn:after{background-color:var(--jw-theme-system-primary-default-bg-color);border-radius:.25rem;transform:scale(.8);transition:transform .15s ease;z-index:-1}.day-btn[data-outside-month]:enabled{color:var(--jw-theme-surface-text-helper-color)}.day-btn[data-outside-month]:enabled[data-selected],.day-btn[data-range-end],.day-btn[data-range-start],.day-btn[data-selected]{color:var(--jw-theme-system-primary-default-color)}.day-btn[data-range-end]:after,.day-btn[data-range-start]:after,.day-btn[data-selected]:after{opacity:1;transform:scale(1)}.day-btn[data-range-end][data-error],.day-btn[data-range-start][data-error],.day-btn[data-selected][data-error]{color:var(--jw-theme-form-validation-error-color)}.day-btn[data-range-end][data-error]:after,.day-btn[data-range-start][data-error]:after,.day-btn[data-selected][data-error]:after{opacity:0;transform:scale(0)}.day-btn[data-in-range]:not([data-range-start]):not([data-range-end]):before{opacity:1}.day-btn[data-in-range]:not([data-range-start]):not([data-range-end]):not([data-selected]):first-child:before{border-radius:.25rem 0 0 .25rem}.day-btn[data-in-range]:not([data-range-start]):not([data-range-end]):not([data-selected]):last-child:before{border-radius:0 .25rem .25rem 0}.day-btn[data-in-range][data-range-start]:not([data-range-end]):before{border-radius:.25rem 0 0 .25rem;opacity:1}.day-btn[data-in-range][data-range-end]:not([data-range-start]):before{border-radius:0 .25rem .25rem 0;opacity:1}.day-btn:hover:not([data-selected]):not([disabled]){color:var(--jw-theme-surface-item-active-color)}.day-btn:hover:not([data-selected]):not([disabled]):after{background-color:initial;border-radius:.25rem;opacity:1;outline:1px solid var(--jw-theme-form-border-regular-focus-border-color);outline-offset:-1px;transform:scale(1)}.day-btn:focus{outline:none}.day-btn[disabled]{color:var(--jw-theme-form-field-disabled-inverted-bg-color);cursor:not-allowed}.day-btn[data-on-max-days]:not([data-selected])[data-on-max-days=start]{--width:-3px}.day-btn[data-on-max-days]:not([data-selected])[data-on-max-days=end]{--width:3px}.day-btn[data-on-max-days]:not([data-selected]):after{background:none;border-radius:0;box-shadow:var(--width) 0 var(--jw-theme-form-field-disabled-inverted-bg-color);opacity:1;outline:none}.day-indicator{bottom:-4px;left:0;position:absolute;right:0}";

const WuiDateInputCalendar = /*@__PURE__*/ proxyCustomElement(class WuiDateInputCalendar extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.clearRangeStartDate = createEvent(this, "clearRangeStartDate", 7);
        this.clearRangeEndDate = createEvent(this, "clearRangeEndDate", 7);
        /**
         * If disabled
         */
        this.disabled = false;
        /**
         * First day of week
         *
         * 0 = Sunday
         * 1 = Monday
         */
        this.weekStartsOn = 0;
        this.initialCalendarDisplayDate = 'today';
        this.displayDate = getInitialDisplayDate(this);
        this.isDateDisabled = (date) => {
            return this.disabled || isDateDisabled(date, this);
        };
        this.isPrevMonthDisabled = () => this.disabled || isPrevMonthDisabled(this.displayDate, this);
        this.isNextMonthDisabled = () => this.disabled || isNextMonthDisabled(this.displayDate, this);
        this.goToPrevMonth = () => {
            this.displayDate = dateFnsExports.subMonths(this.displayDate, 1);
        };
        this.goToNextMonth = () => {
            this.displayDate = dateFnsExports.addMonths(this.displayDate, 1);
        };
        this.selectDate = (date) => {
            this.value = toDateString(date);
            if (this.rangeStartDate && dateFnsExports.isBefore(this.value, this.rangeStartDate)) {
                this.clearRangeStartDate.emit();
            }
            if (this.rangeEndDate && dateFnsExports.isAfter(this.value, this.rangeEndDate)) {
                this.clearRangeEndDate.emit();
            }
            this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.handleMouseEnter = (date) => {
            // Prevents range start to flicker when hovering over a disabled date.
            if (!this.isDateDisabled(date)) {
                this.hoverDate = date;
            }
        };
        this.handleMouseLeave = () => {
            this.hoverDate = undefined;
        };
        this.getDayAttributes = (date, inMonth, isSelected) => {
            return {
                class: 'day-btn',
                'data-range-start': isDateRangeStart(date, this),
                'data-range-end': isDateRangeEnd(date, this),
                'data-in-range': isDateInRange(date, this),
                'data-outside-month': !inMonth,
                'data-selected': isSelected,
                'data-error': Boolean(this.dateRangeError && isSelected),
                'data-on-max-days': isDateOnMaxDays(date, this),
            };
        };
    }
    _value(newValue) {
        const date = toDate(newValue);
        // Only set display date if there's a valid date
        if (date) {
            this.displayDate = date;
        }
    }
    checkSanity() {
        try {
            checkDateOverlaps(this);
            this.dateRangeError = undefined;
        }
        catch (err) {
            this.dateRangeError = err instanceof Error ? err.message : String(err);
        }
    }
    componentWillRender() {
        this.checkSanity();
    }
    componentWillLoad() {
        this.displayDate = getInitialDisplayDate(this);
    }
    render() {
        const weekStartsOn = typeof this.weekStartsOn === 'undefined' ? 0 : this.weekStartsOn;
        const calendarDays = memoizedGetCalendarDays({
            year: this.displayDate.getFullYear(),
            month: this.displayDate.getMonth(),
            weekStartsOn,
        });
        const weekdays = getWeekDayHeaders(weekStartsOn);
        const weeks = segmentMonthInWeeks(calendarDays);
        return (h(Host, { key: 'e86ec312f18ee23994368da9631394c985cc10e2' }, h("input", { key: '31f510d11ab493f988141d5e55c9129351d6ae73', hidden: true, type: "date", value: this.value, name: this.name, onChange: (e) => {
                // Don't bubble this event up, use `input` instead
                e.stopPropagation();
            } }), h("div", { key: 'c1da38f317eb7e4d104ebe7b0115d04c08e1391b', class: "month-header" }, h("wui-button", { key: 'e31249eb2d17d1bf1e2514a072163283a1c20979', icon: "chevron-left", type: "icon", tooltip: "Previous month", disabled: this.isPrevMonthDisabled(), onClick: this.goToPrevMonth }), h("span", { key: '4a67decdc07ab36537bde7d24d8070fc065805cf' }, format('CAL_MONTH_YEAR', this.displayDate)), h("wui-button", { key: '338081d67e3667ec41550940d297c6dfbe38b1a5', icon: "chevron-right", type: "icon", tooltip: "Next month", disabled: this.isNextMonthDisabled(), onClick: this.goToNextMonth })), h("div", { key: '94442efe3df3e890af21642c87fe26dc76d30d1d', class: "weekdays-header" }, weekdays.map((weekday, key) => (h("div", { key: key, class: "weekday-header" }, weekday)))), weeks.map((week, index) => (h("div", { key: `${this.displayDate}-week-${index}`, class: "days-container" }, week.map((day) => {
            const isToday = dateFnsExports.isSameDay(day.date, new Date());
            const isSelected = Boolean(this.value && toDateString(day.date) === this.value);
            const btn = (h("button", { key: day.key, title: day.formatted.full, disabled: this.isDateDisabled(day.date), onMouseEnter: () => this.handleMouseEnter(day.date), onMouseLeave: this.handleMouseLeave, onClick: () => this.selectDate(day.date), type: "button", ...this.getDayAttributes(day.date, day.inMonth, isSelected) }, day.formatted.day, isToday && h("span", { class: "day-indicator" }, "\u2013")));
            if (isSelected && this.dateRangeError) {
                return (h("wui-tooltip", { label: this.dateRangeError, open: false, trigger: "focus" }, btn));
            }
            return btn;
        }))))));
    }
    get elm() { return this; }
    static get watchers() { return {
        "value": ["_value"]
    }; }
    static get style() { return dateInputCalendarCss; }
}, [257, "wui-date-input-calendar", {
        "disabled": [4],
        "weekStartsOn": [2, "week-starts-on"],
        "value": [1025],
        "name": [1],
        "rangeStartDate": [1, "range-start-date"],
        "rangeEndDate": [1, "range-end-date"],
        "min": [1],
        "max": [1],
        "initialCalendarDisplayDate": [1, "initial-calendar-display-date"],
        "maxDays": [2, "max-days"],
        "dateRangeError": [32],
        "hoverDate": [32],
        "displayDate": [32]
    }, undefined, {
        "value": ["_value"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-date-input-calendar", "wui-button", "wui-icon", "wui-loader", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-date-input-calendar":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDateInputCalendar);
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiDateInputCalendar as W, toDateRangeString as a, toDateRange as b, isRangeBeforeDate as c, defineCustomElement as d, exceedsMaxDays as e, isRangeAfterDate as f, isRangeInOrder as i, toDateString as t };
